// src/components/auth/SessionHandler.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, configApiRef, microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { Dialog, DialogContent, DialogActions, Button, Typography } from '@material-ui/core';
import { useServiceRegistryTranslation } from './i18n/ServiceRegistryLanguageProvider';

const IDLE_TIMEOUT = 120 * 60 * 1000; 
const WARNING_BEFORE_TIMEOUT = 5 * 60 * 1000; 
const CHECK_INTERVAL = 60 * 1000; 

export const SessionHandler: React.FC = ({ children }) => {
  const { t, formatNumber } = useServiceRegistryTranslation();
  const navigate = useNavigate();
  const config = useApi(configApiRef);
  const authApi = useApi(microsoftAuthApiRef);
  
  const [lastActivity, setLastActivity] = useState<number>(Date.now());
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState<number>(IDLE_TIMEOUT);

  // Update last activity timestamp
  const updateActivity = () => {
    setLastActivity(Date.now());
    setShowWarning(false);
    setRemainingTime(IDLE_TIMEOUT);
  };

  // Handle session extension
  const extendSession = async () => {
    try {
      await authApi.getBackstageIdentity({
        optional: false,
      });
      updateActivity();
    } catch (error) {
      console.error('Session extension failed:', error);
      handleLogout();
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await authApi.signOut();
      // Force reload the page after logout
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
      window.location.href = '/';
    }
  };

  // Check session status periodically
  useEffect(() => {
    const checkSession = async () => {
      try {
        const identity = await authApi.getBackstageIdentity({
          optional: true,
        });
        
        if (!identity) {
          handleLogout();
          return;
        }

        const currentTime = Date.now();
        const timeSinceLastActivity = currentTime - lastActivity;
        const timeRemaining = IDLE_TIMEOUT - timeSinceLastActivity;
        
        // Show warning before timeout
        if (timeRemaining <= WARNING_BEFORE_TIMEOUT && !showWarning) {
          setShowWarning(true);
        }
        
        // Update remaining time
        if (timeRemaining > 0) {
          setRemainingTime(timeRemaining);
        } else {
          handleLogout();
        }

      } catch (error) {
        console.error('Session check failed:', error);
        handleLogout();
      }
    };

    const interval = setInterval(checkSession, CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, [lastActivity, showWarning]);

  // Set up activity listeners
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, []);

  // Format time for display
  const formatRemainingTime = () => {
    const seconds = Math.max(0, Math.floor(remainingTime / 1000));
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return t('session.minutesSeconds', {
      minutes: formatNumber(minutes),
      seconds: formatNumber(remainingSeconds),
    });
  };

  return (
    <>
      {children}
      
      <Dialog open={showWarning && remainingTime > 0} onClose={extendSession}>
        <DialogContent>
          <Typography>
            {t('session.expiry', { time: formatRemainingTime() })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogout} color="secondary">
            {t('session.logout')}
          </Button>
          <Button onClick={extendSession} color="primary" variant="contained">
            {t('session.staySignedIn')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
